/**
 * Iter 83 — Onboarding Wizard configuration (Super Admin only).
 *
 * Reads/writes the org-wide `onboarding_config` setting via /api/settings/onboarding.
 * Affects every user across the org on their next page load.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpenCheck, Users, ShieldAlert, Sparkles, Eye, EyeOff, RotateCcw, Save } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const DISMISS_KEY = 'ellora_onboarding_dismissed';

export default function OnboardingSettings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cfg, setCfg] = useState({
    wizard_enabled_admin: true,
    wizard_enabled_users: true,
    force_compliance_steps: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isSuper = user?.role === 'super_admin';

  useEffect(() => {
    apiGet('/settings/onboarding')
      .then(d => setCfg(prev => ({ ...prev, ...d })))
      .catch(() => toast.error('Failed to load onboarding config'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiPut('/settings/onboarding', cfg);
      setCfg(res);
      toast.success('Onboarding settings saved · applies to all users');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const relaunchWizard = () => {
    localStorage.removeItem(DISMISS_KEY);
    toast.success('Wizard will appear on next page load');
    setTimeout(() => window.location.href = '/app', 800);
  };

  if (!isSuper) {
    return (
      <div>
        <PageHeader overline="Settings" title="Onboarding Wizard" subtitle="Super Admin only" />
        <div className="bg-rose-50 border border-rose-200 rounded-sm p-6 text-sm text-rose-700 flex gap-3">
          <ShieldAlert size={18} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold mb-1">Access denied</div>
            <div>Only the super admin can change the onboarding wizard behaviour for the entire organisation.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <PageHeader
        overline="Settings · System"
        title="Onboarding Wizard"
        subtitle="Control the post-login wizard for admins and regular users — applies org-wide on next page load."
      />

      {loading ? (
        <div className="text-xs text-slate-400 uppercase tracking-widest py-12 text-center font-display">Loading…</div>
      ) : (
        <div className="space-y-3">
          {/* Toggle 1 — Admin wizard */}
          <Toggle
            icon={Sparkles}
            title="Show wizard to Super Admin"
            subtitle="Auto-launches on login when the workspace is empty (no employees, no projects). Used to guide initial setup. Each super admin can still dismiss it permanently for their own browser."
            value={cfg.wizard_enabled_admin}
            onChange={v => setCfg({ ...cfg, wizard_enabled_admin: v })}
            testid="onboard-toggle-admin"
          />

          {/* Toggle 2 — User wizard */}
          <Toggle
            icon={Users}
            title="Show wizard to regular users"
            subtitle="Appears on every login until the user completes the 4 mandatory steps: profile fields, password change, app tour, HR policy acknowledgements."
            value={cfg.wizard_enabled_users}
            onChange={v => setCfg({ ...cfg, wizard_enabled_users: v })}
            testid="onboard-toggle-users"
          />

          {/* Toggle 3 — Force compliance */}
          <Toggle
            icon={BookOpenCheck}
            title="Force compliance completion"
            subtitle="When ON, the user cannot dismiss the wizard until all 4 mandatory steps are done. Recommended for new joiners onboarding."
            value={cfg.force_compliance_steps}
            onChange={v => setCfg({ ...cfg, force_compliance_steps: v })}
            disabled={!cfg.wizard_enabled_users}
            testid="onboard-toggle-compliance"
          />

          {/* Save button */}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200">
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm text-sm font-display font-bold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
              data-testid="onboard-save-btn"
            >
              <Save size={14} />{saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>

          {/* Manual relaunch for current admin */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 mt-6">
            <div className="kpi-overline mb-1">Personal · this browser only</div>
            <h3 className="font-display font-bold text-base mb-2">Re-launch wizard now</h3>
            <p className="text-xs text-slate-600 mb-3">
              You previously dismissed the wizard on this device. Click below to bring it back — useful if you want to revisit the setup checklist or test the flow before turning it on for others.
            </p>
            <button
              onClick={relaunchWizard}
              className="px-3 py-1.5 border border-[#1F7A6E] text-[#1F7A6E] hover:bg-[#1F7A6E] hover:text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider flex items-center gap-2"
              data-testid="onboard-relaunch-btn"
            >
              <RotateCcw size={13} />Relaunch wizard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({ icon: Icon, title, subtitle, value, onChange, disabled = false, testid }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-sm p-4 flex items-start gap-4 ${disabled ? 'opacity-50' : ''}`}>
      <div className="w-10 h-10 rounded-sm bg-[#1F7A6E]/10 flex items-center justify-center shrink-0">
        <Icon size={18} className="text-[#1F7A6E]" />
      </div>
      <div className="flex-1">
        <h3 className="font-display font-bold text-sm text-slate-900">{title}</h3>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{subtitle}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={disabled}
        onClick={() => onChange(!value)}
        data-testid={testid}
        className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#1F7A6E] focus:ring-offset-2 ${value ? 'bg-emerald-600' : 'bg-slate-300'} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
        <span className="sr-only">{value ? 'On' : 'Off'}</span>
        {value
          ? <Eye size={10} className="text-white absolute right-1.5 pointer-events-none" />
          : <EyeOff size={10} className="text-slate-500 absolute left-1.5 pointer-events-none" />}
      </button>
    </div>
  );
}
